<template>
  <div class="assistant-message">
    <!-- Erreur -->
    <div v-if="message.error" class="assistant-error">{{ message.error }}</div>

    <!-- Demande de clarification -->
    <template v-else-if="reply && reply.clarification">
      <p class="font-semibold mb-2">{{ reply.clarification }}</p>
      <div v-if="reply.clarification_options && reply.clarification_options.length" class="flex flex-wrap gap-2">
        <button
          v-for="opt in reply.clarification_options"
          :key="opt"
          type="button"
          class="assistant-pill-button"
          @click="$emit('follow-up', opt)"
        >
          {{ opt }}
        </button>
      </div>
    </template>

    <!-- Réponse structurée -->
    <template v-else-if="reply">
      <!-- Réponse principale -->
      <div
        class="md-content"
        v-html="renderMarkdown(reply.main, canContactCse)"
        @click="handleMarkdownClick"
      ></div><!-- eslint-disable-line vue/no-v-html -->

      <!-- La section « Voir les détails » est volontairement désactivée. -->

      <!-- Sources (groupées par document) -->
      <div v-if="groupedSources.length" class="assistant-section">
        <div class="assistant-section-title">Sources</div>
        <ul class="space-y-2">
          <li v-for="(group, gIdx) in groupedSources" :key="gIdx">
            <a
              v-if="group.source_url"
              :href="group.source_url"
              target="_blank"
              rel="noopener"
              class="assistant-source-title assistant-source-title--link"
            >{{ group.title }}</a>
            <span v-else class="assistant-source-title">{{ group.title }}</span>

            <ul class="mt-1 space-y-1">
              <li v-for="(it, iIdx) in group.items" :key="iIdx" class="text-xs">
                <button
                  type="button"
                  class="assistant-source-button"
                  @click="it.viewer_url ? openSourceViewer(it.viewer_url) : togglePassage(gIdx, iIdx)"
                >
                  <i
                    class="assistant-quote-icon"
                    :class="it.viewer_url ? 'fas fa-external-link-alt' : 'fas fa-quote-right'"
                  ></i>
                  <template v-if="it.article_ref">Article {{ it.article_ref }}</template>
                  <template v-else>{{ it.viewer_url ? 'Ouvrir le passage dans le document' : 'Voir le passage cité' }}</template>
                  <span v-if="it.page" class="text-gray-400"> · page {{ it.page }}</span>
                  <span v-if="it.similarity" class="text-gray-400"> · {{ Math.round(it.similarity * 100) }}%</span>
                </button>
                <div
                  v-if="!it.viewer_url && isOpen(gIdx, iIdx)"
                  class="assistant-source-passage"
                >{{ it.content }}</div>
              </li>
            </ul>
          </li>
        </ul>
      </div>

      <!-- Mention légale -->
      <p v-if="reply.disclaimer" class="assistant-disclaimer">
        {{ reply.disclaimer }}
      </p>

      <!-- Questions de suivi (max 3) -->
      <div v-if="followUps.length" class="assistant-section">
        <div class="assistant-section-title">Questions similaires</div>
        <div class="flex flex-col gap-2">
          <button
            v-for="q in followUps"
            :key="q"
            type="button"
            class="assistant-follow-up"
            @click="$emit('follow-up', q)"
          >
            {{ q }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { marked } from 'marked'

const CONTACT_CSE_LINK_PATTERN = /\[\[CONTACT_CSE_LINK\]\]([^\r\n]*?)\[\[\/CONTACT_CSE_LINK\]\]/gi
const CONTACT_CSE_MARKER_PATTERN = /\[\[\/?CONTACT_CSE_LINK\]\]/gi

export default {
  name: 'app-ai-assistant-message',
  props: {
    message: { type: Object, required: true }, // { reply, sources, error }
    canContactCse: { type: Boolean, default: false }
  },
  data () {
    return {
      openPassages: {}
    }
  },
  computed: {
    reply () {
      return this.message.reply || null
    },
    followUps () {
      const list = this.reply && this.reply.follow_up_questions
      return Array.isArray(list)
        ? list.filter(q => typeof q === 'string' && q.trim()).slice(0, 3)
        : []
    },
    // Regroupe les passages RAG par document pour éviter de répéter le titre.
    groupedSources () {
      const sources = Array.isArray(this.message.sources) ? this.message.sources : []
      const map = new Map()
      for (const s of sources) {
        const title = s.title || s.doc_title || 'Document'
        const key = s.doc_uuid || title
        if (!map.has(key)) {
          map.set(key, { title, source_url: s.viewer_url || s.source_url || null, items: [] })
        }
        const group = map.get(key)
        if (!group.source_url && (s.viewer_url || s.source_url)) {
          group.source_url = s.viewer_url || s.source_url
        }
        const exists = group.items.some(it => it.article_ref === (s.article_ref || null) && it.content === (s.content || '') && it.viewer_url === (s.viewer_url || null))
        if (!exists) {
          group.items.push({
            article_ref: s.article_ref || null,
            content: s.content || '',
            viewer_url: s.viewer_url || null,
            page: s.page || null,
            similarity: typeof s.similarity === 'number' ? s.similarity : null
          })
        }
      }
      return Array.from(map.values())
    }
  },
  methods: {
    renderMarkdown (text, linkContactCse = false) {
      if (!text) {
        return ''
      }

      const content = text
        .replace(
          CONTACT_CSE_LINK_PATTERN,
          linkContactCse ? '[$1](#contact-cse)' : '$1'
        )
        .replace(CONTACT_CSE_MARKER_PATTERN, '')

      return marked.parse(content, { breaks: true })
    },
    handleMarkdownClick (event) {
      const target = event.target
      if (!(target instanceof Element) || !target.closest('a[href="#contact-cse"]')) {
        return
      }

      event.preventDefault()
    },
    passageKey (gIdx, iIdx) {
      return gIdx + '-' + iIdx
    },
    isOpen (gIdx, iIdx) {
      return !!this.openPassages[this.passageKey(gIdx, iIdx)]
    },
    togglePassage (gIdx, iIdx) {
      const key = this.passageKey(gIdx, iIdx)
      this.$set(this.openPassages, key, !this.openPassages[key])
    },
    openSourceViewer (url) {
      if (!url) {
        return
      }
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
.assistant-message {
  max-width: min(46rem, 88%);
  border: 1px solid #cbd5e1;
  border-radius: 0.75rem 0.75rem 0.75rem 0.25rem;
  background: #ffffff;
  color: #1f2937;
  padding: 0.95rem 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
}

.assistant-error {
  color: #dc2626;
  font-weight: 700;
}

.assistant-section {
  margin-top: 0.85rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.assistant-section-title {
  margin-bottom: 0.5rem;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.1;
  text-transform: uppercase;
}

.assistant-pill-button,
.assistant-follow-up {
  border: 1px solid #bfdbfe;
  border-radius: 0.5rem;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 700;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.assistant-pill-button {
  padding: 0.35rem 0.7rem;
  font-size: 0.8125rem;
}

.assistant-follow-up {
  width: 100%;
  padding: 0.65rem 0.75rem;
  text-align: left;
  font-size: 0.875rem;
}

.assistant-pill-button:hover,
.assistant-follow-up:hover {
  border-color: #2563eb;
  background: #2563eb;
  color: #ffffff;
}

.assistant-link-button,
.assistant-source-button {
  color: #1d4ed8;
  font-weight: 700;
}

.assistant-link-button {
  font-size: 0.75rem;
}

.assistant-link-button:hover,
.assistant-source-button:hover,
.assistant-source-title--link:hover {
  text-decoration: underline;
}

.assistant-source-title {
  color: #111827;
  font-weight: 800;
}

.assistant-source-title--link {
  color: #1d4ed8;
}

.assistant-source-button {
  font-size: 0.75rem;
}

.assistant-quote-icon {
  margin-right: 0.25rem;
  font-size: 0.625rem;
}

.assistant-source-passage {
  margin-top: 0.4rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #f8fafc;
  color: #334155;
  padding: 0.65rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

.assistant-disclaimer {
  margin-top: 0.85rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
  color: #64748b;
  font-size: 0.75rem;
  font-style: italic;
}

.md-content >>> p { margin: 0 0 0.6rem 0; }
.md-content >>> p:last-child { margin-bottom: 0; }
.md-content >>> strong { color: #1d4ed8; font-weight: 700; }
.md-content >>> ul { list-style: disc; padding-left: 1.25rem; margin: 0.4rem 0; }
.md-content >>> ol { list-style: decimal; padding-left: 1.25rem; margin: 0.4rem 0; }
.md-content >>> li { margin: 0.15rem 0; }
.md-content >>> h1,
.md-content >>> h2,
.md-content >>> h3 { font-weight: 700; margin: 0.6rem 0 0.3rem; }
.md-content >>> a { color: #2563eb; text-decoration: underline; }
.md-content >>> code { background: #f3f4f6; padding: 0.1rem 0.3rem; border-radius: 4px; }

@media (max-width: 768px) {
  .assistant-message {
    max-width: 100%;
  }
}
</style>
